
@if(previewMode() !== 'none' && selectedPrescription(); as p) {
    <!-- PRINT PREVIEW OVERLAY (Common Container) -->
    <div class="print-preview-container bg-slate-500 fixed inset-0 z-[100] p-4 sm:p-8 print:p-0 print:bg-white pb-32 overflow-y-auto custom-scrollbar">
        
        <!-- Mode 1: Prescription Form -->
        @if(previewMode() === 'prescription') {
            <app-prescription-form [prescriptionId]="p.id"></app-prescription-form>
        } 
        
        <!-- Mode 2: Medical Certificate -->
        @if(previewMode() === 'certificate') {
            <!-- Pass a callback to close via output event if we added it, OR just let it render. 
                 The MedicalCertificate component has its own internal toolbar, 
                 so we might need to hide the external toolbar below if mode is certificate. -->
            <app-medical-certificate [prescriptionId]="p.id" (close)="closePreview()"></app-medical-certificate>
        }
    </div>
    
    <!-- EXTERNAL TOOLBAR (Only show for Prescription Form, Certificate has its own internal one) -->
    @if(previewMode() === 'prescription') {
        <div class="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 p-3 shadow-lg z-[101] no-print">
            <div class="max-w-screen-xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3">
                <div class="text-white">
                    <p class="font-bold text-emerald-400">ตัวอย่างก่อนพิมพ์ (Print Preview)</p>
                    <p class="text-xs opacity-70 hidden sm:block">เอกสารใบสั่งยา (Prescription)</p>
                </div>
                <div class="flex gap-3 w-full sm:w-auto">
                    <button (click)="closePreview()" class="flex-1 sm:flex-none bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg font-bold transition-colors">
                        <i class="fa-solid fa-times mr-2"></i> ปิด
                    </button>
                    
                    <button (click)="downloadImage()" [disabled]="isGeneratingImage()" class="flex-1 sm:flex-none bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-lg font-bold shadow-lg flex items-center justify-center gap-2 transition-transform transform active:scale-95 disabled:opacity-50">
                        @if(isGeneratingImage()) {
                            <i class="fa-solid fa-spinner animate-spin"></i>
                            <span>กำลังสร้าง...</span>
                        } @else {
                            <i class="fa-solid fa-image"></i>
                            <span>บันทึก PNG</span>
                        }
                    </button>
                    
                    <button [disabled]="true" class="flex-1 sm:flex-none bg-gray-500 text-white/50 px-8 py-3 rounded-lg font-bold shadow-none flex items-center justify-center gap-2 cursor-not-allowed">
                        <i class="fa-solid fa-print"></i> พิมพ์เอกสาร (Print)
                    </button>
                </div>
            </div>
        </div>
    }
} @else {
    <!-- NORMAL PHARMACY UI (Main Screen) -->
    <div class="flex flex-col space-y-4 no-print h-[calc(100vh-120px)]">
        
        <!-- Header -->
        <div class="flex justify-between items-center flex-shrink-0">
            <div>
                <h1 class="text-2xl font-bold text-gray-800"><i class="fa-solid fa-pills mr-2 text-emerald-600"></i>{{ 'backOffice.pharmacy.title' | translate }}</h1>
                <p class="text-gray-500 text-sm">{{ 'backOffice.pharmacy.subtitle' | translate }}</p>
            </div>
            <button (click)="loadQueue()" class="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-bold shadow-sm transition-all text-sm">
                <i class="fa-solid fa-rotate mr-2"></i> {{ 'backOffice.pharmacy.refresh' | translate }}
            </button>
        </div>

        <div class="flex flex-col lg:flex-row gap-4 flex-grow min-h-0 overflow-hidden">
            
            <!-- List / Queue (Scrollable) -->
            <div id="pharmacy-queue-list" class="w-full lg:w-1/3 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
                <!-- Queue Header & Search -->
                <div class="p-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                    <div class="flex justify-between items-center mb-2">
                        <span class="font-bold text-gray-700">{{ 'backOffice.pharmacy.queue' | translate }} ({{ filteredQueue().length }})</span>
                        <span class="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">{{ 'backOffice.pharmacy.statusApproved' | translate }}</span>
                    </div>
                    <!-- Search Input -->
                    <div class="relative">
                        <i class="fa-solid fa-search absolute left-3 top-2.5 text-gray-400 text-xs"></i>
                        <input type="text" [(ngModel)]="queueSearchTerm" [placeholder]="'backOffice.pharmacy.searchQueue' | translate" 
                               class="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-emerald-500 focus:border-emerald-500">
                    </div>
                </div>

                <!-- Queue Items -->
                <div class="overflow-y-auto flex-grow p-2 space-y-2 custom-scrollbar">
                    @for(p of filteredQueue(); track p.id) {
                        <div (click)="selectItem(p)" 
                            [class]="'p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md group ' + (selectedPrescription()?.id === p.id ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500' : 'border-gray-200 bg-white')">
                            <div class="flex justify-between items-start mb-1">
                                <h4 class="font-bold text-gray-800 text-sm group-hover:text-emerald-700">{{ p.customerName }}</h4>
                                <span class="text-[10px] text-gray-500 font-mono">{{ p.reviewedAt | date:'HH:mm' }}</span>
                            </div>
                            <div class="flex items-center justify-between mt-1">
                                <span class="font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-xs">
                                    {{ p.approvedItems?.totalAmount }} {{ p.approvedItems?.dosageUnit }}
                                </span>
                                <span class="text-[10px] text-gray-500 border border-gray-200 px-2 py-0.5 rounded max-w-[100px] truncate">
                                    {{ p.approvedItems?.symptoms || 'General' }}
                                </span>
                            </div>
                        </div>
                    } @empty {
                        <div class="text-center py-10 text-gray-400">
                            <i class="fa-solid fa-check-circle text-4xl mb-2 opacity-50"></i>
                            <p class="text-sm">{{ 'backOffice.pharmacy.empty' | translate }}</p>
                        </div>
                    }
                </div>
            </div>

            <!-- Detail & Actions (Scrollable Content + Fixed Footer) -->
            <div id="prescription-detail-panel" class="w-full lg:w-2/3 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden relative">
                @if(selectedPrescription(); as p) {
                    <!-- Scrollable Content Area -->
                    <div class="p-6 overflow-y-auto flex-grow custom-scrollbar">
                        <!-- Header Info -->
                        <div class="flex justify-between items-start border-b pb-4 mb-4">
                            <div>
                                <h2 class="text-2xl font-bold text-gray-800">{{ p.customerName }}</h2>
                                <p class="text-sm text-gray-500">ID: {{ p.customerCitizenId }}</p>
                            </div>
                            <div class="text-right">
                                <div class="text-xs font-bold text-emerald-600 border border-emerald-200 bg-emerald-50 px-2 py-1 rounded inline-block mb-1">
                                    {{ 'backOffice.pharmacy.approvedLabel' | translate }}
                                </div>
                                <p class="text-xs text-gray-400">Dr. {{ p.doctorName }}</p>
                            </div>
                        </div>

                        <div class="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6">
                            <h3 class="text-blue-900 font-bold mb-3 flex items-center gap-2 text-sm">
                                <i class="fa-solid fa-clipboard-list"></i> คำสั่งแพทย์ (Doctor's Order)
                            </h3>
                            
                            <div class="bg-white p-4 rounded-lg shadow-sm space-y-3">
                                <div class="flex justify-between items-center border-b border-gray-100 pb-3">
                                    <span class="text-gray-500 font-medium text-sm">จำนวนที่อนุมัติ (Quantity)</span>
                                    <div class="text-right">
                                        <span class="text-3xl font-black text-blue-600">{{ p.approvedItems?.totalAmount }}</span>
                                        <span class="text-xs font-bold text-gray-500 uppercase ml-1">{{ p.approvedItems?.dosageUnit }}</span>
                                    </div>
                                </div>
                                <div class="pt-1">
                                    <span class="block text-gray-400 text-[10px] uppercase mb-1">วิธีใช้ (Instructions)</span>
                                    <div class="p-3 bg-gray-50 rounded text-gray-800 font-medium border border-gray-100 text-sm">
                                        {{ p.approvedItems?.usageInstructions || '-' }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="space-y-2 pl-2 border-l-2 border-gray-200 ml-2">
                            <h3 class="font-bold text-gray-700 text-xs uppercase tracking-wide mb-2">ขั้นตอนการจ่ายยา (Compliance Process)</h3>
                            <ol class="list-none space-y-2 text-sm text-gray-600">
                                <li class="flex gap-3 items-start"><span class="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">1</span><span>ตรวจสอบใบรับรองแพทย์</span></li>
                                <li class="flex gap-3 items-start"><span class="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">2</span><span>จัดเตรียมยาตามจำนวนที่แพทย์สั่ง</span></li>
                                <li class="flex gap-3 items-start"><span class="bg-emerald-100 text-emerald-700 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">3</span><span class="font-bold text-emerald-700">บันทึกล็อต (Lot Number) ลงในระบบเพื่อรายงาน อย.</span></li>
                            </ol>
                        </div>
                    </div>

                    <!-- Fixed Footer (Sticky Buttons) -->
                    <div class="p-4 bg-gray-50 border-t border-gray-200 flex flex-wrap justify-end gap-3 flex-shrink-0">
                        <!-- Modified Click Events -->
                        <button (click)="enterPrescriptionPreview(p)" class="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-100 shadow-sm flex items-center gap-2 text-sm">
                            <i class="fa-solid fa-file-prescription text-blue-600"></i> ใบสั่งยา
                        </button>
                        
                        <button (click)="enterCertificatePreview(p)" class="px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-lg shadow-sm flex items-center gap-2 text-sm">
                            <i class="fa-solid fa-certificate"></i> ใบรับรองแพทย์
                        </button>

                        <button id="dispense-action-btn" (click)="openDispenseModal(p)" class="px-6 py-2.5 bg-emerald-600 text-white font-bold rounded-lg hover:bg-emerald-700 shadow-lg flex items-center gap-2 animate-pulse text-sm">
                            <i class="fa-solid fa-file-signature"></i>
                            {{ 'backOffice.pharmacy.dispenseButton' | translate }}
                        </button>
                    </div>
                } @else {
                    <div class="flex flex-col items-center justify-center h-full text-gray-400">
                        <i class="fa-solid fa-hand-pointer text-5xl mb-4 opacity-50"></i>
                        <p class="text-lg">{{ 'backOffice.pharmacy.selectPrompt' | translate }}</p>
                    </div>
                }
            </div>
        </div>
    </div>

    <!-- Dispensing Modal -->
    @if(isDispenseModalOpen()) {
        <div class="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
            <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-fade-in-up">
                <div class="bg-emerald-600 p-4 flex justify-between items-center text-white">
                    <h3 class="text-lg font-bold"><i class="fa-solid fa-clipboard-check mr-2"></i> {{ 'backOffice.pharmacy.dispenseModalTitle' | translate }}</h3>
                    <button (click)="closeDispenseModal()" class="text-white/80 hover:text-white"><i class="fa-solid fa-times text-xl"></i></button>
                </div>
                
                <form [formGroup]="dispenseForm" (ngSubmit)="confirmDispense()" class="p-6 space-y-4">
                    <div class="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-sm text-yellow-800 mb-4">
                        <strong>Compliance Note:</strong> บันทึกนี้จะถูกส่งไปยังรายงาน ภท.28 โดยอัตโนมัติ
                    </div>
                    <div>
                        <label class="block text-sm font-bold text-gray-700 mb-1">วัตถุประสงค์การนำไปใช้ (Purpose) <span class="text-red-500">*</span></label>
                        <select formControlName="purpose" class="w-full border border-gray-300 rounded-md p-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white font-bold text-emerald-700">
                            <option value="TREATMENT">ใช้เอง / เพื่อการบำบัดรักษาโรค (Own Treatment)</option>
                            <option value="RESEARCH">เพื่อการศึกษาวิจัย (Research)</option>
                            <option value="PROCESSING">เพื่อการแปรรูป (Processing)</option>
                            <option value="EXPORT">เพื่อการส่งออก (Export)</option>
                        </select>
                        <p class="text-xs text-gray-500 mt-1">* สำหรับคลินิกโดยทั่วไปเลือก "ใช้เอง/รักษาโรค"</p>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">{{ 'backOffice.pharmacy.modal.quantity' | translate }} <span class="text-red-500">*</span></label>
                            <input type="number" step="0.01" formControlName="quantity" class="w-full border border-gray-300 rounded-md p-2 focus:ring-emerald-500 focus:border-emerald-500">
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">{{ 'backOffice.pharmacy.modal.unit' | translate }}</label>
                            <select formControlName="unit" class="w-full border border-gray-300 rounded-md p-2 bg-white">
                                <option value="GRAM">กรัม (Gram)</option>
                                <option value="PIECE">ชิ้น (Piece)</option>
                                <option value="ML">มิลลิลิตร (ml)</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">{{ 'backOffice.pharmacy.modal.lot' | translate }}</label>
                        <input type="text" formControlName="lotNumber" class="w-full border border-gray-300 rounded-md p-2 focus:ring-emerald-500 focus:border-emerald-500" [placeholder]="'backOffice.pharmacy.modal.lotPlaceholder' | translate">
                    </div>
                    <div class="pt-4 flex justify-end gap-3">
                        <button type="button" (click)="closeDispenseModal()" class="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md font-medium">{{ 'common.cancel' | translate }}</button>
                        <button type="submit" [disabled]="dispenseForm.invalid || isProcessing()" class="px-6 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 font-bold shadow disabled:bg-gray-400">
                            @if(isProcessing()) { <i class="fa-solid fa-spinner animate-spin"></i> }
                            {{ 'backOffice.pharmacy.modal.confirm' | translate }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    }
}
<style>
@media print {
    .no-print { display: none !important; }
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 20px;
}
</style>
